import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Award, Search, Building2, MapPin, ChevronLeft, ExternalLink, CheckCircle,
  Shield, Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicInstituicoesCertificadasPage() {
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [verificarId, setVerificarId] = useState('');
  const [verificacaoResult, setVerificacaoResult] = useState(null);
  const [verificando, setVerificando] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/instituicoes-certificadas`);
      const data = await response.json();
      setInstituicoes(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificar = async () => {
    if (!verificarId.trim()) return;
    
    setVerificando(true);
    setVerificacaoResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/public/verificar-certificado/${verificarId.trim()}`);
      const data = await response.json();
      setVerificacaoResult(data);
    } catch (error) {
      setVerificacaoResult({ valido: false, mensagem: 'Erro ao verificar certificado' });
    } finally {
      setVerificando(false);
    }
  };

  const filteredInstituicoes = instituicoes.filter(inst =>
    !searchTerm || 
    inst.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.distrito_nome?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-yellow-400 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center gap-2 text-amber-100 mb-4">
            <Link to="/publico" className="hover:text-white flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" />
              Portal Público
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Award className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Instituições Certificadas EA</h1>
              <p className="text-amber-100 mt-1">
                Lista de instituições com certificação ENTRAJUDA ativa
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-md">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{instituicoes.length}</p>
              <p className="text-sm text-amber-100">Certificadas</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">
                {instituicoes.filter(i => i.ultima_classificacao === 'Excelente').length}
              </p>
              <p className="text-sm text-amber-100">Excelentes</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">
                {[...new Set(instituicoes.map(i => i.distrito_nome))].length}
              </p>
              <p className="text-sm text-amber-100">Distritos</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Pesquisar instituição ou distrito..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-12 h-12 text-lg"
              />
            </div>

            {/* List */}
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-8 h-8 border-2 border-slate-200 border-t-amber-500 rounded-full animate-spin"></div>
              </div>
            ) : filteredInstituicoes.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center text-slate-500">
                  <Award className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Nenhuma instituição encontrada</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {filteredInstituicoes.map(inst => (
                  <Card key={inst.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-1 bg-gradient-to-r from-amber-500 to-yellow-400"></div>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 className="w-5 h-5 text-amber-500" />
                            <h3 className="font-bold text-lg">{inst.nome}</h3>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {inst.distrito_nome || '-'}
                            </span>
                            <span>{inst.tipo_entidade}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${
                            inst.ultima_classificacao === 'Excelente' ? 'text-green-600' : 'text-[#0066CC]'
                          }`}>
                            {inst.ultimo_score_avaliacao?.toFixed(1)}
                          </div>
                          <Badge variant="outline" className="mt-1">{inst.ultima_classificacao}</Badge>
                        </div>
                      </div>

                      <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                        <div className="flex items-center gap-1 text-slate-500">
                          <Award className="w-4 h-4 text-amber-500" />
                          <span className="font-mono">{inst.certificado_ea_id}</span>
                        </div>
                        <div className="flex items-center gap-1 text-slate-500">
                          <Calendar className="w-3 h-3" />
                          Válido até {new Date(inst.validade_certificacao).toLocaleDateString('pt-PT')}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar - Verificação */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-[#0066CC]" />
                  Verificar Certificado
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-slate-500">
                  Introduza o ID do certificado para verificar a sua autenticidade.
                </p>
                <div className="flex gap-2">
                  <Input
                    placeholder="CERT-EA-2026-XXXXXXXX"
                    value={verificarId}
                    onChange={e => setVerificarId(e.target.value)}
                    className="font-mono"
                  />
                  <Button onClick={handleVerificar} disabled={verificando} className="bg-[#0066CC]">
                    {verificando ? '...' : 'Verificar'}
                  </Button>
                </div>

                {verificacaoResult && (
                  <div className={`p-4 rounded-lg ${
                    verificacaoResult.valido 
                      ? 'bg-green-50 border border-green-200 dark:bg-green-900/20' 
                      : 'bg-red-50 border border-red-200 dark:bg-red-900/20'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {verificacaoResult.valido ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Shield className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-bold ${verificacaoResult.valido ? 'text-green-600' : 'text-red-600'}`}>
                        {verificacaoResult.mensagem}
                      </span>
                    </div>
                    
                    {verificacaoResult.certificado && (
                      <div className="space-y-2 text-sm">
                        <p><strong>Instituição:</strong> {verificacaoResult.certificado.instituicao_nome}</p>
                        <p><strong>Score:</strong> {verificacaoResult.certificado.score}</p>
                        <p><strong>Classificação:</strong> {verificacaoResult.certificado.classificacao}</p>
                        <p><strong>Emitido:</strong> {new Date(verificacaoResult.certificado.data_emissao).toLocaleDateString('pt-PT')}</p>
                        <p><strong>Válido até:</strong> {new Date(verificacaoResult.certificado.data_validade).toLocaleDateString('pt-PT')}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sobre a Certificação EA</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm text-slate-600 dark:text-slate-400">
                <p>
                  A Certificação EA é atribuída a instituições que demonstram um elevado 
                  padrão de qualidade nas suas operações e serviços.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span><strong>Excelente:</strong> Score ≥ 80</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-[#0066CC]"></div>
                    <span><strong>Bom:</strong> Score ≥ 60</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Os certificados têm validade de 2 anos e podem ser verificados nesta página.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-100 dark:bg-slate-800 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} ENTRAJUDATA - Portal Público de Instituições Certificadas</p>
          <div className="mt-4 flex justify-center gap-4">
            <Link to="/publico" className="hover:text-[#0066CC]">Portal Público</Link>
            <Link to="/login" className="hover:text-[#0066CC]">Área Privada</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
